/**
 * Manages SharedArrayBuffer pool for hot-reloading programs
 */

import {
  BytecodeProgram,
  ControlIndex,
  createSharedBuffers,
  hashString,
  NUM_PROGRAM_BUFFERS,
  SEQUENCE_BUFFER_SIZE,
  SequenceData,
} from './bytecode.ts'

import type { SharedBuffers } from './bytecode.ts'
export type { SharedBuffers }

export class BufferManager {
  private buffers: SharedBuffers
  private control: Int32Array
  private programs: Float64Array[]
  private sequences: Float64Array[]
  private workletProgram: Float64Array
  private workletProgramA: Float64Array
  private workletProgramB: Float64Array
  private workletSequences: Float64Array[]
  private currentProgramIndex = 0

  constructor() {
    this.buffers = createSharedBuffers()
    this.control = new Int32Array(this.buffers.control)
    this.programs = this.buffers.programs.map(buf => new Float64Array(buf))
    this.sequences = this.buffers.sequences.map(buf => new Float64Array(buf))
    this.workletProgram = new Float64Array(this.buffers.workletProgram)
    this.workletProgramA = new Float64Array(this.buffers.workletProgramA)
    this.workletProgramB = new Float64Array(this.buffers.workletProgramB)
    this.workletSequences = this.buffers.workletSequences.map(buf => new Float64Array(buf))

    // Initialize control buffer
    this.control[ControlIndex.ACTIVE_PROGRAM] = 0
    this.control[ControlIndex.IS_PLAYING] = 0
    this.control[ControlIndex.BPM] = 12000 // 120.00 * 100
    this.control[ControlIndex.PROGRAM_READY] = 0
    this.control[ControlIndex.SEQUENCE_COUNT] = 0
    Atomics.store(this.control, ControlIndex.WORKLET_LOAD_READY, 0)
    Atomics.store(this.control, ControlIndex.WORKLET_LOAD_DONE, 0)
    Atomics.store(this.control, ControlIndex.SYNC_MODE, 0) // Default to 0 bars (now)
    Atomics.store(this.control, ControlIndex.CROSSFADE, 50) // 0.5 default
    Atomics.store(this.control, ControlIndex.GLOBAL_BPM, 12000) // 120.00 * 100
    Atomics.store(this.control, ControlIndex.TUNE, 1000) // 1.0 default (no change)
    Atomics.store(this.control, ControlIndex.DUAL_CHANNEL_MODE, 0) // Single channel by default
    Atomics.store(this.control, ControlIndex.OVERSAMPLING, 1) // x1 oversampling by default
    Atomics.store(this.control, ControlIndex.BPM_LOCK_SINGLE, 0) // BPM not locked by default
    Atomics.store(this.control, ControlIndex.GLOBAL_SAMPLE_COUNTER, 0) // Initialize sample counter to 0
  }

  getSharedBuffers(): SharedBuffers {
    return this.buffers
  }

  /**
   * Load a new program into the next available buffer and atomically switch to it
   */
  loadProgram(program: BytecodeProgram): void {
    // Set to single channel mode
    Atomics.store(this.control, ControlIndex.DUAL_CHANNEL_MODE, 0)

    // Get next buffer index (ring buffer)
    const nextIndex = (this.currentProgramIndex + 1) % NUM_PROGRAM_BUFFERS

    // Write bytecode length to control buffer for this program
    Atomics.store(this.control, 5 + nextIndex, program.bytecode.length)

    // Write bytecode to next buffer
    const targetBuffer = this.programs[nextIndex]
    targetBuffer.set(program.bytecode)

    // Write sequences to SharedArrayBuffers
    this.writeSequences(program.sequences)

    // Update BPM and tune
    Atomics.store(this.control, ControlIndex.BPM, Math.floor(program.bpm * 100))
    Atomics.store(this.control, ControlIndex.TUNE, Math.floor(program.tune * 1000))

    // Set BPM lock flag: lock if program has explicit BPM declaration
    Atomics.store(this.control, ControlIndex.BPM_LOCK_SINGLE, program.hasExplicitBpm ? 1 : 0)

    // Store sequence count
    Atomics.store(this.control, ControlIndex.SEQUENCE_COUNT, program.sequences.length)

    // Atomically switch to new program
    Atomics.store(this.control, ControlIndex.ACTIVE_PROGRAM, nextIndex)

    // Set program ready flag (processor will clear it after loading)
    Atomics.store(this.control, ControlIndex.PROGRAM_READY, 1)

    this.currentProgramIndex = nextIndex
  }

  private writeSequences(sequences: SequenceData[]): void {
    // Clear all sequence buffers first (write 0 to first slot)
    for (let i = 0; i < this.sequences.length; i++) {
      this.sequences[i][0] = 0
    }

    // Sort sequences by ID to ensure they're written in ID order
    const sortedSequences = [...sequences].sort((a, b) => a.id - b.id)

    // Write sequences to buffers indexed by their ID
    for (const seq of sortedSequences) {
      // Use sequence ID as buffer index (sequences now have incremental IDs: 0, 1, 2, ...)
      if (seq.id >= 0 && seq.id < this.sequences.length) {
        const buffer = this.sequences[seq.id]
        let offset = 0

        // Write sequence ID
        buffer[offset++] = seq.id

        // Write note count
        buffer[offset++] = seq.notes.length

        // Write swing value
        buffer[offset++] = seq.swing ?? 0

        // Write offset value
        buffer[offset++] = seq.offset ?? 0

        // Write exponent value
        buffer[offset++] = seq.exponent ?? 1

        // Write each note
        for (const note of seq.notes) {
          // Pitch count
          buffer[offset++] = note.pitches.length

          // Pitches
          for (let j = 0; j < note.pitches.length; j++) {
            buffer[offset++] = note.pitches[j]
          }

          // Duration, velocity, hold, and glide
          buffer[offset++] = note.duration
          buffer[offset++] = note.velocity
          buffer[offset++] = note.hold
          buffer[offset++] = note.glide ?? 0
        }
      }
    }
  }

  start(): void {
    Atomics.store(this.control, ControlIndex.IS_PLAYING, 1)
  }

  stop(): void {
    Atomics.store(this.control, ControlIndex.IS_PLAYING, 0)
  }

  isPlaying(): boolean {
    return Atomics.load(this.control, ControlIndex.IS_PLAYING) === 1
  }

  setSyncMode(bars: number): void {
    Atomics.store(this.control, ControlIndex.SYNC_MODE, Math.max(0, Math.min(9, bars)))
  }

  getSyncMode(): number {
    return Atomics.load(this.control, ControlIndex.SYNC_MODE)
  }

  setCrossfade(value: number): void {
    Atomics.store(this.control, ControlIndex.CROSSFADE, Math.floor(value * 100))
  }

  setGlobalBpm(value: number): void {
    Atomics.store(this.control, ControlIndex.GLOBAL_BPM, Math.floor(value * 100))
    // Also update BPM for single mode only if not locked
    const isLocked = Atomics.load(this.control, ControlIndex.BPM_LOCK_SINGLE) === 1
    if (!isLocked) {
      Atomics.store(this.control, ControlIndex.BPM, Math.floor(value * 100))
    }
  }

  setTune(value: number): void {
    Atomics.store(this.control, ControlIndex.TUNE, Math.floor(value * 1000))
  }

  setOversampling(value: 1 | 2 | 4 | 8): void {
    Atomics.store(this.control, ControlIndex.OVERSAMPLING, value)
  }

  getGlobalSampleCounter(): number {
    return Atomics.load(this.control, ControlIndex.GLOBAL_SAMPLE_COUNTER)
  }

  loadDualProgram(
    programA: BytecodeProgram,
    programB: BytecodeProgram,
    globalBpm: number,
    crossfade: number,
  ): void {
    // Set dual channel mode
    Atomics.store(this.control, ControlIndex.DUAL_CHANNEL_MODE, 1)
    Atomics.store(this.control, ControlIndex.GLOBAL_BPM, Math.floor(globalBpm * 100))
    Atomics.store(this.control, ControlIndex.CROSSFADE, Math.floor(crossfade * 100))

    // Store bytecode for both programs in worklet buffers
    this.workletProgramA.set(programA.bytecode)
    this.workletProgramB.set(programB.bytecode)

    Atomics.store(this.control, ControlIndex.WORKLET_PROGRAMA_LENGTH, programA.bytecode.length)
    Atomics.store(this.control, ControlIndex.WORKLET_PROGRAMB_LENGTH, programB.bytecode.length)

    // Write sequences for both programs (A gets first 32 slots, B gets next 32)
    this.writeDualSequences(programA.sequences, programB.sequences)

    // Update BPM and sequence counts
    Atomics.store(this.control, ControlIndex.BPM, Math.floor(globalBpm * 100))

    // Use tune from programA if it's not the default, otherwise use the global tune value
    const tuneValue = programA.tune !== 1.0 ? programA.tune : programB.tune
    Atomics.store(this.control, ControlIndex.TUNE, Math.floor(tuneValue * 1000))

    // Set program ready flag
    Atomics.store(this.control, ControlIndex.PROGRAM_READY, 1)
  }

  private writeDualSequences(sequencesA: SequenceData[], sequencesB: SequenceData[]): void {
    // Clear all sequence buffers first
    for (let i = 0; i < this.sequences.length; i++) {
      this.sequences[i][0] = 0
    }

    // Write sequences A to first 32 slots
    for (let i = 0; i < sequencesA.length && i < 32; i++) {
      const seq = sequencesA[i]
      const buffer = this.sequences[i]
      let offset = 0

      buffer[offset++] = seq.id
      buffer[offset++] = seq.notes.length
      buffer[offset++] = seq.swing ?? 0
      buffer[offset++] = seq.offset ?? 0
      buffer[offset++] = seq.exponent ?? 1

      for (const note of seq.notes) {
        buffer[offset++] = note.pitches.length
        for (let j = 0; j < note.pitches.length; j++) {
          buffer[offset++] = note.pitches[j]
        }
        buffer[offset++] = note.duration
        buffer[offset++] = note.velocity
        buffer[offset++] = note.hold
        buffer[offset++] = note.glide ?? 0
      }
    }

    // Write sequences B to next 32 slots
    for (let i = 0; i < sequencesB.length && i < 32; i++) {
      const seq = sequencesB[i]
      const buffer = this.sequences[i + 32]
      let offset = 0

      buffer[offset++] = seq.id
      buffer[offset++] = seq.notes.length
      buffer[offset++] = seq.swing ?? 0
      buffer[offset++] = seq.offset ?? 0
      buffer[offset++] = seq.exponent ?? 1

      for (const note of seq.notes) {
        buffer[offset++] = note.pitches.length
        for (let j = 0; j < note.pitches.length; j++) {
          buffer[offset++] = note.pitches[j]
        }
        buffer[offset++] = note.duration
        buffer[offset++] = note.velocity
        buffer[offset++] = note.hold
        buffer[offset++] = note.glide ?? 0
      }
    }
  }

  getCurrentProgram(): {
    bytecode: Float64Array
    bpm: number
    length: number
    sequenceCount: number
  } | null {
    const activeIndex = Atomics.load(this.control, ControlIndex.ACTIVE_PROGRAM)
    const bpm = Atomics.load(this.control, ControlIndex.BPM) / 100
    const length = Atomics.load(this.control, 5 + activeIndex)
    const sequenceCount = Atomics.load(this.control, ControlIndex.SEQUENCE_COUNT)
    const bpmLock = Atomics.load(this.control, ControlIndex.BPM_LOCK_SINGLE)
    const globalBpm = Atomics.load(this.control, ControlIndex.GLOBAL_BPM) / 100

    console.log(`[BufferManager.getCurrentProgram] BPM=${bpm}, GLOBAL_BPM=${globalBpm}, BPM_LOCK=${bpmLock}`)

    if (length > 0) {
      return {
        bytecode: this.programs[activeIndex],
        bpm,
        length,
        sequenceCount,
      }
    }

    return null
  }

  /**
   * Get current BPM from control buffer (written by worklet)
   * This doesn't require a program to be loaded, just reads the current BPM value
   */
  getBpm(): number {
    return Atomics.load(this.control, ControlIndex.BPM) / 100
  }

  /**
   * Get global BPM from control buffer (not locked to program BPM)
   */
  getGlobalBpm(): number {
    return Atomics.load(this.control, ControlIndex.GLOBAL_BPM) / 100
  }

  getSequenceData(): Float64Array[] {
    return this.sequences
  }

  /**
   * Prepare data for worklet to load (writes to SharedArrayBuffers)
   * This runs in the main thread and does all the data preparation work
   */
  prepareWorkletLoad(): void {
    const isDualChannel = Atomics.load(this.control, ControlIndex.DUAL_CHANNEL_MODE) === 1

    if (isDualChannel) {
      this.prepareDualWorkletLoad()
    }
    else {
      const prog = this.getCurrentProgram()
      if (!prog) return

      const syncBars = Atomics.load(this.control, ControlIndex.SYNC_MODE)

      // If sync mode is active, allow overwriting previous load request
      // Otherwise wait for previous load to complete
      if (syncBars === 0) {
        while (Atomics.load(this.control, ControlIndex.WORKLET_LOAD_READY) === 1) {
          // Spin wait (should be very quick)
        }
      }

      // Copy program data to worklet buffer
      this.workletProgram.set(prog.bytecode.subarray(0, prog.length))
      Atomics.store(this.control, ControlIndex.WORKLET_PROGRAM_LENGTH, prog.length)

      // Copy sequence data to worklet buffers
      // Sequences are now stored in buffers indexed by their ID, so we need to find all non-empty buffers
      let seqCount = 0
      for (let i = 0; i < this.sequences.length; i++) {
        const buf = this.sequences[i]
        // Check if buffer has a sequence: buf[0] is the sequence ID, buf[1] is the note count
        // If note count > 0, there's a sequence (even if ID is 0, which is valid)
        if (buf && buf[1] > 0) {
          const seqId = buf[0]
          const noteCount = buf[1]
          // Verify the ID matches the buffer index (sequences are stored by ID)
          if (seqId === i) {
            let len = 5 // id + note count + swing + offset + exponent
            let off = 5 // start after id, note count, swing, offset, and exponent
            // Validate noteCount is reasonable
            if (noteCount < 0 || noteCount > 10000) {
              console.error(`[BufferManager] Invalid noteCount: ${noteCount} for sequence ${i}`)
              continue
            }
            for (let n = 0; n < noteCount; n++) {
              if (off >= buf.length) {
                console.error(`[BufferManager] Sequence ${i} buffer overflow at note ${n}`)
                break
              }
              const pitchCount = buf[off]
              // Validate pitchCount is reasonable (max 256 for safety)
              if (pitchCount < 0 || pitchCount > 256) {
                console.error(`[BufferManager] Invalid pitchCount: ${pitchCount} for sequence ${i}, note ${n}`)
                break
              }
              const noteSize = 1 + pitchCount + 3 // pitchCount + pitches + duration + velocity + hold
              if (len + noteSize > SEQUENCE_BUFFER_SIZE) {
                console.error(`[BufferManager] Sequence ${i} length would exceed buffer size: ${len + noteSize}`)
                break
              }
              len += noteSize
              off += noteSize
            }
            // Final validation
            if (len <= 0 || len > SEQUENCE_BUFFER_SIZE) {
              console.error(`[BufferManager] Invalid calculated sequence length: ${len} for sequence ${i}`)
              continue
            }
            this.workletSequences[i].set(buf.subarray(0, len))
            Atomics.store(this.control, ControlIndex.WORKLET_SEQ_LENGTHS + i, len)
            seqCount++
          }
        }
      }

      Atomics.store(this.control, ControlIndex.WORKLET_SEQUENCE_COUNT, seqCount)

      // Signal worklet that data is ready
      Atomics.store(this.control, ControlIndex.WORKLET_LOAD_READY, 1)
    }
  }

  private prepareDualWorkletLoad(): void {
    const syncBars = Atomics.load(this.control, ControlIndex.SYNC_MODE)

    // If sync mode is active, allow overwriting previous load request
    // Otherwise wait for previous load to complete
    if (syncBars === 0) {
      while (Atomics.load(this.control, ControlIndex.WORKLET_LOAD_READY) === 1) {
        // Spin wait (should be very quick)
      }
    }

    // Note: In dual channel mode, the bytecode data is stored directly in sequences
    // We'll need to extract it when needed
    // For now, just copy the sequence data

    // Copy sequences A (first 32 slots)
    let seqCountA = 0
    for (let i = 0; i < 32; i++) {
      const buf = this.sequences[i]
      // Check if buffer has a sequence: buf[0] is the sequence ID, buf[1] is the note count
      // If note count > 0, there's a sequence (even if ID is 0, which is valid)
      if (buf && buf[1] > 0) {
        const noteCount = buf[1]
        // Validate noteCount is reasonable
        if (noteCount < 0 || noteCount > 10000) {
          console.error(`[BufferManager] Invalid noteCount: ${noteCount} for sequence A ${i}`)
          continue
        }
        let len = 5 // id + note count + swing + offset + exponent
        let off = 5 // start after id, note count, swing, offset, and exponent
        for (let n = 0; n < noteCount; n++) {
          if (off >= buf.length) {
            console.error(`[BufferManager] Sequence A ${i} buffer overflow at note ${n}`)
            break
          }
          const pitchCount = buf[off]
          // Validate pitchCount is reasonable (max 256 for safety)
          if (pitchCount < 0 || pitchCount > 256) {
            console.error(`[BufferManager] Invalid pitchCount: ${pitchCount} for sequence A ${i}, note ${n}`)
            break
          }
          const noteSize = 1 + pitchCount + 3 // pitchCount + pitches + duration + velocity + hold
          if (len + noteSize > SEQUENCE_BUFFER_SIZE) {
            console.error(`[BufferManager] Sequence A ${i} length would exceed buffer size: ${len + noteSize}`)
            break
          }
          len += noteSize
          off += noteSize
        }
        // Final validation
        if (len <= 0 || len > SEQUENCE_BUFFER_SIZE) {
          console.error(`[BufferManager] Invalid calculated sequence A length: ${len} for sequence ${i}`)
          continue
        }
        this.workletSequences[i].set(buf.subarray(0, len))
        Atomics.store(this.control, ControlIndex.WORKLET_SEQ_LENGTHS + i, len)
        seqCountA++
      }
    }

    // Copy sequences B (next 32 slots)
    let seqCountB = 0
    for (let i = 0; i < 32; i++) {
      const buf = this.sequences[i + 32]
      // Check if buffer has a sequence: buf[0] is the sequence ID, buf[1] is the note count
      // If note count > 0, there's a sequence (even if ID is 0, which is valid)
      if (buf && buf[1] > 0) {
        const noteCount = buf[1]
        // Validate noteCount is reasonable
        if (noteCount < 0 || noteCount > 10000) {
          console.error(`[BufferManager] Invalid noteCount: ${noteCount} for sequence B ${i}`)
          continue
        }
        let len = 5 // id + note count + swing + offset + exponent
        let off = 5 // start after id, note count, swing, offset, and exponent
        for (let n = 0; n < noteCount; n++) {
          if (off >= buf.length) {
            console.error(`[BufferManager] Sequence B ${i} buffer overflow at note ${n}`)
            break
          }
          const pitchCount = buf[off]
          // Validate pitchCount is reasonable (max 256 for safety)
          if (pitchCount < 0 || pitchCount > 256) {
            console.error(`[BufferManager] Invalid pitchCount: ${pitchCount} for sequence B ${i}, note ${n}`)
            break
          }
          const noteSize = 1 + pitchCount + 3 // pitchCount + pitches + duration + velocity + hold
          if (len + noteSize > SEQUENCE_BUFFER_SIZE) {
            console.error(`[BufferManager] Sequence B ${i} length would exceed buffer size: ${len + noteSize}`)
            break
          }
          len += noteSize
          off += noteSize
        }
        // Final validation
        if (len <= 0 || len > SEQUENCE_BUFFER_SIZE) {
          console.error(`[BufferManager] Invalid calculated sequence B length: ${len} for sequence ${i}`)
          continue
        }
        this.workletSequences[i + 32].set(buf.subarray(0, len))
        Atomics.store(this.control, ControlIndex.WORKLET_SEQ_LENGTHS + 32 + i, len)
        seqCountB++
      }
    }

    Atomics.store(this.control, ControlIndex.WORKLET_SEQUENCE_COUNT_A, seqCountA)
    Atomics.store(this.control, ControlIndex.WORKLET_SEQUENCE_COUNT_B, seqCountB)

    // Signal worklet that data is ready
    Atomics.store(this.control, ControlIndex.WORKLET_LOAD_READY, 1)
  }
}

/**
 * Worklet-side reader for SharedArrayBuffers
 */
export class BufferReader {
  private control: Int32Array
  private programs: Float64Array[]
  private sequences: Float64Array[]
  private currentProgramIndex = -1

  constructor(buffers: SharedBuffers) {
    this.control = new Int32Array(buffers.control)
    this.programs = buffers.programs.map(buf => new Float64Array(buf))
    this.sequences = buffers.sequences.map(buf => new Float64Array(buf))
  }

  /**
   * Check if program is ready to load (simple flag check)
   */
  checkProgramReady(): boolean {
    return Atomics.load(this.control, ControlIndex.PROGRAM_READY) === 1
  }

  /**
   * Get current program data from SharedArrayBuffers
   */
  getCurrentProgram(): {
    bytecode: Float64Array
    bpm: number
    length: number
    sequenceCount: number
  } | null {
    const activeIndex = Atomics.load(this.control, ControlIndex.ACTIVE_PROGRAM)
    const bpm = Atomics.load(this.control, ControlIndex.BPM) / 100
    const length = Atomics.load(this.control, 5 + activeIndex)
    const sequenceCount = Atomics.load(this.control, ControlIndex.SEQUENCE_COUNT)

    if (length > 0) {
      return {
        bytecode: this.programs[activeIndex],
        bpm,
        length,
        sequenceCount,
      }
    }

    return null
  }

  /**
   * Mark program as loaded (clear ready flag)
   */
  markProgramLoaded(): void {
    Atomics.store(this.control, ControlIndex.PROGRAM_READY, 0)
  }

  /**
   * Read sequence by index (from SharedArrayBuffer)
   */
  getSequence(index: number): SequenceData | null {
    if (index < 0 || index >= this.sequences.length) {
      return null
    }

    const buffer = this.sequences[index]
    if (buffer[0] === 0) {
      return null // Empty sequence slot
    }

    return this.parseSequence(buffer)
  }

  /**
   * Get sequence buffers for direct access
   */
  getSequenceBuffers(): Float64Array[] {
    return this.sequences
  }

  /**
   * Get program buffers for direct access
   */
  getProgramBuffers(): Float64Array[] {
    return this.programs
  }

  isPlaying(): boolean {
    return Atomics.load(this.control, ControlIndex.IS_PLAYING) === 1
  }

  getBPM(): number {
    return Atomics.load(this.control, ControlIndex.BPM) / 100
  }

  /**
   * Read a sequence by its hashed ID
   */
  readSequence(id: number): SequenceData | null {
    for (const buffer of this.sequences) {
      if (buffer[0] === id) {
        return this.parseSequence(buffer)
      }
    }
    return null
  }

  private parseSequence(buffer: Float64Array): SequenceData {
    let offset = 0
    const id = buffer[offset++]
    const noteCount = buffer[offset++]
    const notes = []

    for (let i = 0; i < noteCount; i++) {
      const pitchCount = buffer[offset++]
      const pitches = new Float32Array(pitchCount)

      for (let j = 0; j < pitchCount; j++) {
        pitches[j] = buffer[offset++]
      }

      const duration = buffer[offset++]
      const velocity = buffer[offset++]

      notes.push({ pitches, duration, hold: 0, velocity })
    }

    return { id, notes }
  }
}
